import * as React from "react"
import { createPortal } from "react-dom"
import { format, subDays } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { DateRange, Matcher } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Calendar } from "@/components/ui/calendar"

export interface DateRangePickerProps {
    value?: DateRange
    onChange: (range: DateRange | undefined) => void
    className?: string
    /** Minimum selectable date (dates before this will be disabled) */
    minDate?: Date
    /** Maximum selectable date (dates after this will be disabled), defaults to today */
    maxDate?: Date
    /** Whether the picker is disabled (e.g., no data available) */
    disabled?: boolean
    /** Placeholder text when no data is available */
    placeholder?: string
}

export function DateRangePicker({
    value,
    onChange,
    className,
    minDate,
    maxDate: maxDateProp,
    disabled = false,
    placeholder,
}: DateRangePickerProps) {
    const [open, setOpen] = React.useState(false)
    const triggerRef = React.useRef<HTMLButtonElement>(null)
    const [position, setPosition] = React.useState({ top: 0, left: 0 })
    
    // Internal state to track selection in progress
    const [pendingRange, setPendingRange] = React.useState<DateRange | undefined>(undefined)

    // Use provided maxDate or default to today
    const maxDate = maxDateProp ?? new Date()

    // Default: last 7 days (bounded by min/max if provided)
    const defaultRange: DateRange = React.useMemo(() => {
        const to = maxDate
        let from = subDays(to, 7)
        // Ensure from is not before minDate
        if (minDate && from < minDate) {
            from = minDate
        }
        return { from, to }
    }, [minDate, maxDate])

    const displayedRange = value ?? defaultRange
    const calendarRange = open ? (pendingRange ?? displayedRange) : displayedRange

    // Build disabled matcher for dates outside the allowed range
    const disabledMatcher: Matcher[] = React.useMemo(() => {
        const matchers: Matcher[] = []
        if (maxDate) matchers.push({ after: maxDate })
        if (minDate) matchers.push({ before: minDate })
        return matchers
    }, [minDate, maxDate])

    // Update position when opening
    React.useLayoutEffect(() => {
        if (open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect()
            setPosition({
                top: rect.bottom + 6,
                left: rect.left
            })
        }
    }, [open])

    // Handle Escape key
    React.useEffect(() => {
        if (!open) return
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setOpen(false)
            }
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [open])

    const handleTriggerClick = () => {
        if (!open) {
            setPendingRange(displayedRange)
            setOpen(true)
        } else {
            setOpen(false)
        }
    }

    const handleSelect = (range: DateRange | undefined) => {
        setPendingRange(range)
        
        // Only close when BOTH dates are selected
        if (range?.from && range?.to) {
            onChange(range)
            setOpen(false)
        }
    }

    const handleReset = () => {
        // Reset to default range (last 7 days bounded by min/max)
        setPendingRange(defaultRange)
        onChange(defaultRange)
        setOpen(false)
    }

    const handleBackdropClick = () => {
        setOpen(false)
    }

    // If disabled, show placeholder
    if (disabled) {
        return (
            <div className={cn(
                "flex h-8 items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold whitespace-nowrap",
                "bg-muted/30 cursor-not-allowed",
                "border-border/40 text-muted-foreground",
                className
            )}>
                <CalendarIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground/50" />
                <span className="truncate">{placeholder || "No data available"}</span>
            </div>
        )
    }

    return (
        <div className="relative">
            <button
                ref={triggerRef}
                type="button"
                onClick={handleTriggerClick}
                className={cn(
                    "flex h-8 items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold whitespace-nowrap",
                    "bg-white/50 backdrop-blur-sm transition-colors duration-150 dark:bg-white/10",
                    "border-white/40 hover:border-primary/30 hover:bg-white/70 dark:border-white/20 dark:hover:bg-white/15",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                    "text-foreground hover:text-foreground",
                    open && "border-primary/50 ring-1 ring-primary/20",
                    className
                )}
            >
                <CalendarIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                <span className="truncate">
                    {displayedRange?.from ? (
                        displayedRange.to ? (
                            <>
                                {format(displayedRange.from, "MMM d")} - {format(displayedRange.to, "MMM d")}
                            </>
                        ) : (
                            format(displayedRange.from, "MMM d")
                        )
                    ) : (
                        "Select dates"
                    )}
                </span>
            </button>

            {open && createPortal(
                <>
                    {/* Backdrop */}
                    <div 
                        className="fixed inset-0 z-[9998]" 
                        onClick={handleBackdropClick}
                    />
                    {/* Calendar dropdown */}
                    <div
                        className="fixed z-[9999] rounded-xl border border-border/50 bg-background/95 shadow-xl backdrop-blur-2xl"
                        style={{ top: position.top, left: position.left }}
                    >
                        <Calendar
                            mode="range"
                            defaultMonth={calendarRange?.to ?? calendarRange?.from ?? maxDate}
                            selected={calendarRange}
                            onSelect={handleSelect}
                            numberOfMonths={1}
                            disabled={disabledMatcher}
                            onReset={handleReset}
                        />
                    </div>
                </>,
                document.body
            )}
        </div>
    )
}
